import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {TimelineItem} from '../timeline-item/timeline-item';
import {SUBTITLE_TRACK_HEIGHT, TimelineItemType} from '../../timeline.types';
import type {ResizeModifyingData, ResizeCalculationParams, ResizeCalculationResult, DragModifyingData} from '../timeline-item/timeline-item.types';
import {MIN_TIME_GAP_FOR_TRANSCRIPT} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import styles from './subtitle-timeline-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {setPushedSubtitlePositions} from '../../timeline-reducer';
import {selectItem, getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';

interface SubtitleTimelineItemProps {
  id: string;
  transcriptId: string;
  startTime: number;
  endTime: number;
  subRow: number;
  onClick?: (id: string, transcriptId: string) => void;
}

interface SubtitlePosition {
  id: string;
  startTime: number;
  endTime: number;
}

/**
 * Minimal shape needed from a subtitles hashmap for pure data calculations.
 * Both the Redux `TranscriptItemObject` and the whiteboard `TranscriptItem`
 * satisfy this structure.
 */
type SubtitleHashmap = Record<string, {startTime: number; endTime: number}>;

// ────────────────────────────────────────────────────────────────
// Pure data helpers – accept SubtitleHashmap, no whiteboard access
// ────────────────────────────────────────────────────────────────

const getSortedSubtitles = (hashmap: SubtitleHashmap, excludeId?: string, preferFirstId?: string): SubtitlePosition[] => {
  return Object.entries(hashmap)
    .filter(([subtitleId]) => !excludeId || subtitleId !== excludeId)
    .map(([subtitleId, subtitle]) => ({
      id: subtitleId,
      startTime: subtitle.startTime,
      endTime: subtitle.endTime,
    }))
    .sort((a, b) => {
      if (a.startTime !== b.startTime) return a.startTime - b.startTime;
      if (preferFirstId) {
        if (a.id === preferFirstId) return -1;
        if (b.id === preferFirstId) return 1;
      }
      return 0;
    });
};

/**
 * Compute pushed positions for neighbors displaced by a resize.
 * Iterates outward from the resized subtitle, cascading pushes to maintain MIN_TIME_GAP_FOR_TRANSCRIPT.
 */
const computePushedPositions = (
  allSubtitles: SubtitlePosition[],
  currentIdx: number,
  newStartTime: number,
  newEndTime: number,
  handle: 'left' | 'right'
): Record<string, {startTime: number; endTime: number}> => {
  const positions: Record<string, {startTime: number; endTime: number}> = {};

  if (handle === 'left') {
    let requiredEndTime = newStartTime - MIN_TIME_GAP_FOR_TRANSCRIPT;
    for (let i = currentIdx - 1; i >= 0; i -= 1) {
      const sub = allSubtitles[i];
      if (sub.endTime <= requiredEndTime) {
        break;
      }
      const duration = sub.endTime - sub.startTime;
      positions[sub.id] = {startTime: requiredEndTime - duration, endTime: requiredEndTime};
      requiredEndTime = requiredEndTime - duration - MIN_TIME_GAP_FOR_TRANSCRIPT;
    }
  } else {
    let requiredStartTime = newEndTime + MIN_TIME_GAP_FOR_TRANSCRIPT;
    for (let i = currentIdx + 1; i < allSubtitles.length; i += 1) {
      const sub = allSubtitles[i];
      if (sub.startTime >= requiredStartTime) {
        break;
      }
      const duration = sub.endTime - sub.startTime;
      positions[sub.id] = {startTime: requiredStartTime, endTime: requiredStartTime + duration};
      requiredStartTime = requiredStartTime + duration + MIN_TIME_GAP_FOR_TRANSCRIPT;
    }
  }

  return positions;
};

/**
 * Calculate the maximum a subtitle can expand to the left before pushing neighbors past 0.
 */
const calculateMaxLeftExpansion = (hashmap: SubtitleHashmap, subtitleId: string): number => {
  const allSubtitles = getSortedSubtitles(hashmap);
  const currentIdx = allSubtitles.findIndex((s) => s.id === subtitleId);
  if (currentIdx === -1) {
    return 0;
  }

  const currentSub = allSubtitles[currentIdx];
  const neighbors = allSubtitles.slice(0, currentIdx);
  const totalNeighborSpace = neighbors.reduce((sum, s) => sum + (s.endTime - s.startTime), 0) + neighbors.length * MIN_TIME_GAP_FOR_TRANSCRIPT;

  return Math.max(0, currentSub.startTime - totalNeighborSpace);
};

// ────────────────────────────────────────────────────────────────
// Whiteboard helpers – require TranscriptItem instance
// ────────────────────────────────────────────────────────────────

/** Returns the live TranscriptItem instance from the whiteboard. */
const getTranscriptInstance = (transcriptId: string): TranscriptItem | undefined => {
  return window.posterEditor?.whiteboard?.getItemForId(transcriptId) as TranscriptItem | undefined;
};

const findOverlappingSubtitle = (subtitles: SubtitlePosition[], startTime: number, endTime: number): SubtitlePosition | undefined => {
  return subtitles.find((s) => startTime < s.endTime && endTime > s.startTime);
};

const calculatePositionBefore = (overlappingSubtitle: SubtitlePosition, duration: number, otherSubtitles: SubtitlePosition[]): {startTime: number; endTime: number} => {
  let finalEndTime = overlappingSubtitle.startTime - MIN_TIME_GAP_FOR_TRANSCRIPT;
  let finalStartTime = finalEndTime - duration;

  const overlappingIndex = otherSubtitles.findIndex((s) => s.id === overlappingSubtitle.id);
  const previousSubtitle = overlappingIndex > 0 ? otherSubtitles[overlappingIndex - 1] : null;

  if (previousSubtitle && finalStartTime < previousSubtitle.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT) {
    finalStartTime = previousSubtitle.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT;
    finalEndTime = finalStartTime + duration;
  }

  if (finalStartTime < 0) {
    finalStartTime = 0;
    finalEndTime = duration;
  }

  return {startTime: finalStartTime, endTime: finalEndTime};
};

const calculatePositionAfter = (overlappingSubtitle: SubtitlePosition, duration: number): {startTime: number; endTime: number} => {
  const finalStartTime = overlappingSubtitle.endTime + MIN_TIME_GAP_FOR_TRANSCRIPT;
  return {startTime: finalStartTime, endTime: finalStartTime + duration};
};

const shouldPlaceBefore = (overlappingSubtitle: SubtitlePosition, cursorTime: number): boolean => {
  const midpoint = overlappingSubtitle.startTime + (overlappingSubtitle.endTime - overlappingSubtitle.startTime) / 2;
  return cursorTime < midpoint;
};

const shiftSubsequentSubtitles = async (transcript: TranscriptItem, movedSubtitleId: string): Promise<void> => {
  const allSubtitles = getSortedSubtitles(transcript.subtitlesHashmap, undefined, movedSubtitleId);
  const movedIndex = allSubtitles.findIndex((s) => s.id === movedSubtitleId);

  if (movedIndex === -1) {
    return;
  }

  let cumulativeShift = 0;
  const updatePromises: Promise<void>[] = [];

  for (let i = movedIndex + 1; i < allSubtitles.length; i++) {
    const current = allSubtitles[i];
    const previous = allSubtitles[i - 1];
    const gap = current.startTime - previous.endTime;

    if (gap < MIN_TIME_GAP_FOR_TRANSCRIPT) {
      cumulativeShift += MIN_TIME_GAP_FOR_TRANSCRIPT - gap;
    }

    if (cumulativeShift > 0) {
      const subtitleToShift = transcript.subtitlesHashmap[current.id];
      if (subtitleToShift) {
        updatePromises.push(
          subtitleToShift.updateFromObject(
            {startTime: current.startTime + cumulativeShift, endTime: current.endTime + cumulativeShift, hasUserEdited: true},
            {undoable: false, updateRedux: false}
          )
        );
      }
    }
  }

  await Promise.all(updatePromises);
};

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────

export function SubtitleTimelineItem({id, transcriptId, startTime, endTime, subRow, onClick}: SubtitleTimelineItemProps): ReactElement | null {
  const dispatch = useAppDispatch();
  // transcriptItem comes from Redux — used for pure data reads (hashmap, startTime/endTime)
  const transcriptItem = useAppSelector((state) => selectItem(state, transcriptId)) as TranscriptItemObject | undefined;
  const captionItem = transcriptItem?.subtitlesHashmap[id];
  const currentPageId = useAppSelector((state) => state.posterEditor.currentPageId);
  const currentPageDuration = useAppSelector(getCurrentPageDuration) ?? 0;
  const activeItemIds = useAppSelector((state) => state.posterEditor.activeItemIds);
  const selectedItemIds = (currentPageId && activeItemIds?.[currentPageId]) ?? [];
  const selectedTimelineItem = useAppSelector((state) => state.timeline.selectedTimelineItem);
  const pushedPosition = useAppSelector((state) => state.timeline.pushedSubtitlePositions[id]);
  const effectiveStartTime = pushedPosition?.startTime ?? startTime;
  const effectiveEndTime = pushedPosition?.endTime ?? endTime;

  // Use the Redux hashmap for hot-path calculations (called on every pointermove).
  // Only fetch the whiteboard instance in async handlers where we need updateFromObject.
  const handleCalculateResize = (params: ResizeCalculationParams): ResizeCalculationResult => {
    const hashmap = transcriptItem?.subtitlesHashmap;
    const {originalStartTime, originalEndTime, deltaPixels, handle, pixelsPerSecond} = params;
    const deltaTime = deltaPixels / pixelsPerSecond;

    if (handle === 'left') {
      let newStart = originalStartTime + deltaTime;
      newStart = Math.min(newStart, originalEndTime - MIN_TIME_GAP_FOR_TRANSCRIPT);
      if (hashmap && newStart < originalStartTime) {
        newStart = Math.max(newStart, originalStartTime - calculateMaxLeftExpansion(hashmap, id));
      }
      return {startTime: Math.max(0, newStart), endTime: originalEndTime};
    }

    let newEnd = originalEndTime + deltaTime;
    newEnd = Math.max(newEnd, originalStartTime + MIN_TIME_GAP_FOR_TRANSCRIPT);
    return {startTime: originalStartTime, endTime: newEnd};
  };

  if (!captionItem || !transcriptItem) {
    return null;
  }

  const isTranscriptSelected = selectedItemIds.includes(transcriptId);
  const isSelected = isTranscriptSelected && selectedTimelineItem?.type === TimelineItemType.SUBTITLE && selectedTimelineItem?.id === id;

  const handleClick = (itemId: string): void => {
    onClick?.(itemId, transcriptId);
  };

  const handleResizeMove = (itemId: string, data: ResizeModifyingData): void => {
    if (!transcriptItem) {
      return;
    }

    const allSubtitles = getSortedSubtitles(transcriptItem.subtitlesHashmap);
    const currentIdx = allSubtitles.findIndex((s) => s.id === itemId);
    if (currentIdx === -1) {
      return;
    }

    dispatch(setPushedSubtitlePositions(computePushedPositions(allSubtitles, currentIdx, data.startTime, data.endTime, data.handle)));
  };

  const handleResizeEnd = async (itemId: string, data: ResizeModifyingData): Promise<void> => {
    const transcript = getTranscriptInstance(transcriptId);
    if (!transcript) {
      return;
    }

    const subtitleItem = transcript.subtitlesHashmap[itemId];
    if (!subtitleItem) {
      return;
    }

    const {startTime: newStartTime, endTime: newEndTime, handle} = data;
    const allSubtitles = getSortedSubtitles(transcript.subtitlesHashmap);
    const currentIdx = allSubtitles.findIndex((s) => s.id === itemId);
    if (currentIdx === -1) {
      return;
    }

    const pushedPositions = computePushedPositions(allSubtitles, currentIdx, newStartTime, newEndTime, handle);
    const updatePromises: Promise<void>[] = [
      subtitleItem.updateFromObject({startTime: newStartTime, endTime: newEndTime, hasUserEdited: true}, {undoable: false, updateRedux: false}),
    ];

    for (const [subtitleId, position] of Object.entries(pushedPositions)) {
      const subtitleToShift = transcript.subtitlesHashmap[subtitleId];
      if (subtitleToShift) {
        updatePromises.push(
          subtitleToShift.updateFromObject({startTime: position.startTime, endTime: position.endTime, hasUserEdited: true}, {undoable: false, updateRedux: false})
        );
      }
    }

    await Promise.all(updatePromises);

    if (handle === 'right' && currentPageId) {
      const updatedSubtitles = getSortedSubtitles(transcript.subtitlesHashmap);
      const lastEndTime = updatedSubtitles.length > 0 ? updatedSubtitles[updatedSubtitles.length - 1].endTime : 0;
      if (lastEndTime > currentPageDuration) {
        const page = window.posterEditor?.whiteboard?.pages.pagesHashMap?.[currentPageId];
        if (page) {
          await page.updateFromObject({duration: lastEndTime, isCustomDuration: true});
        }
      }
    }

    transcript.page.poster.history.addPosterHistory();
    transcript.page.poster.redux.updateReduxData();
  };

  const handleDragEnd = async (subtitleId: string, data: DragModifyingData): Promise<void> => {
    const transcript = getTranscriptInstance(transcriptId);
    if (!transcript) {
      return;
    }

    const subtitleItem = transcript.subtitlesHashmap[subtitleId];
    if (!subtitleItem) {
      return;
    }

    const {startTime: newStartTime, endTime: newEndTime, cursorTime} = data;
    const duration = newEndTime - newStartTime;

    const otherSubtitles = getSortedSubtitles(transcript.subtitlesHashmap, subtitleId);
    const overlappingSubtitle = findOverlappingSubtitle(otherSubtitles, newStartTime, newEndTime);

    let finalPosition = {startTime: newStartTime, endTime: newEndTime};

    if (overlappingSubtitle) {
      finalPosition = shouldPlaceBefore(overlappingSubtitle, cursorTime)
        ? calculatePositionBefore(overlappingSubtitle, duration, otherSubtitles)
        : calculatePositionAfter(overlappingSubtitle, duration);
    }

    await subtitleItem.updateFromObject(
      {startTime: finalPosition.startTime, endTime: finalPosition.endTime, hasUserEdited: true},
      {undoable: false, updateRedux: false}
    );

    await shiftSubsequentSubtitles(transcript, subtitleId);

    if (finalPosition.endTime > currentPageDuration && currentPageId) {
      const page = window.posterEditor?.whiteboard?.pages.pagesHashMap?.[currentPageId];
      if (page) {
        await page.updateFromObject({duration: finalPosition.endTime, isCustomDuration: true});
      }
    }

    transcript.page.poster.history.addPosterHistory();
    transcript.page.poster.redux.updateReduxData();
  };

  return (
    <TimelineItem
      id={id}
      startTime={effectiveStartTime}
      endTime={effectiveEndTime}
      minDuration={0.1}
      subRow={subRow}
      variant={TimelineItemType.SUBTITLE}
      label={captionItem.text}
      trackHeight={SUBTITLE_TRACK_HEIGHT}
      isSelected={isSelected}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onResizeMove={handleResizeMove}
      onResizeEnd={handleResizeEnd}
      onCalculateResize={handleCalculateResize}
    >
      <div className={styles.content}>
        <Icon icon={captionItem.hasUserEdited ? 'icon-captions' : 'icon-ai-subtitles'} size={IconSize.SIZE_ICON_16} className={styles.icon} />
        <Text className={styles.text} val={captionItem.text} size={TextSize.XXSMALL} />
      </div>
    </TimelineItem>
  );
}
